import clsx from "clsx";
import React from "react";
import {
  ButtonBase,
  ButtonBaseProps,
  MenuItem,
  ListItemText,
  Box,
  ListItemIcon,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {
  usePopupState,
  bindTrigger,
  bindMenu,
} from "material-ui-popup-state/hooks";
import { useDropzone } from "react-dropzone";

import { ReactComponent as ImageFileAddIcon } from "../../icons/ImageFileAdd.svg";
import { Menu } from "../menu/Menu";
import { useUploadFile } from "../../hooks/useUploadFile";
import { SOMETHING_WENT_WRONG, AssetType } from "../../constants";
import { ElementType } from "../editor/types/elements";
import { useEditorProgram } from "../new-editor/hooks";

import { ImageButton } from "./ImageButton";
import { MoreMenuButton } from "./MoreMenuButton";
import { getMimeTypes } from "../../utils/file";

import { LaptopMinimal, Globe, Repeat } from "lucide-react";

const useStyles = makeStyles((theme) => ({
  root: {},

  empty: {
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: theme.palette.text.secondary,
  },

  disabled: {
    opacity: 0.5,
  },

  icon: {
    color: theme.palette.text.secondary,
  },

  menuPopover: {
    zIndex: "99999 !important" as any,
  },

  primaryText: {
    fontWeight: 500,
    fontSize: 14,
  },

  image: {
    position: "relative",

    "&:hover $more": {
      display: "block",
    },
  },

  menuOpen: {
    "& $more": {
      display: "block",
    },
  },

  more: {
    display: "none",
    position: "absolute",
    right: theme.spacing(2),
    top: theme.spacing(2),
  },
}));

export interface CustomImageButtonProps
  extends Omit<ButtonBaseProps, "src" | "onSelect" | "onError"> {
  src?: string;
  onSelect?: (value: string) => void;
  onRequestImage?: () => void;
  onError?: (error: string) => void;
}

export function CustomImageButton(props: CustomImageButtonProps) {
  const {
    className,
    onSelect,
    onRequestImage,
    src: selectedSrc,
    onError,
  } = props;
  const s = useStyles();
  const { programId } = useEditorProgram() ?? { programId: 0 };
  const [upload, _, isUploading] = useUploadFile({
    id: programId,
    refType: "Program",
    getAssetType: () => AssetType.PROGRAM_IMAGE,
    onUpload: ({ url }) => {
      setSrc(url);
      if (onSelect) {
        onSelect(url);
      }
    },
    onError: () => onError(SOMETHING_WENT_WRONG),
  });
  const [src, setSrc] = React.useState(selectedSrc);

  const menuState = usePopupState({
    variant: "popover",
    popupId: "custom-image-menu",
  });

  React.useEffect(() => {
    onError(null);
  }, [menuState.isOpen]);

  const onUpload = React.useCallback(
    async (files: File[]) => {
      menuState.close();
      upload(files[0]);
    },
    [menuState, onSelect, upload],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onUpload,
    accept: getMimeTypes(ElementType.IMAGE),
  });

  React.useEffect(() => {
    if (selectedSrc) {
      setSrc(selectedSrc);
    }
  }, [selectedSrc]);

  const handleClick = React.useCallback(() => {
    onSelect(src);
  }, [onSelect, src]);

  const handleChangeImage = React.useCallback(() => {
    menuState.close();
    setSrc(null);
    if (onSelect) {
      onSelect(null);
    }
  }, [menuState, onSelect]);

  return (
    <>
      {src ? (
        <Box className={clsx(s.image, menuState.isOpen && s.menuOpen)}>
          <ImageButton
            className={className}
            selected={src === selectedSrc}
            src={src}
            onClick={handleClick}
          />
          <MoreMenuButton className={s.more} {...bindTrigger(menuState)} />
        </Box>
      ) : (
        <ButtonBase
          className={clsx(className, s.empty, isUploading && s.disabled)}
          disabled={isUploading}
          {...bindTrigger(menuState)}
        >
          <ImageFileAddIcon className={s.icon} />
        </ButtonBase>
      )}

      <Menu
        PopoverClasses={{
          root: s.menuPopover,
        }}
        {...bindMenu(menuState)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {!src && (
          <MenuItem {...(getRootProps() as any)} dense role="menuitem">
            <input
              {...getInputProps()}
              accept="image/*"
              id="image-file"
              type="file"
            />

            <ListItemIcon children={<LaptopMinimal />} />

            <ListItemText
              classes={{ primary: s.primaryText }}
              primary="From computer"
            />
          </MenuItem>
        )}
        {!src && (
          <MenuItem onClick={onRequestImage} dense>
            <ListItemIcon children={<Globe />} />
            <ListItemText
              classes={{ primary: s.primaryText }}
              primary="Unsplash"
            />
          </MenuItem>
        )}
        {src && (
          <MenuItem onClick={handleChangeImage} dense>
            <ListItemIcon children={<Repeat />} />
            <ListItemText
              classes={{ primary: s.primaryText }}
              primary="Change image"
            />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
